"use client";
import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  useTheme,
  Stack,
} from "@mui/material";

// for employees

import { styled } from "@mui/material/styles";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import SearchIcon from "@mui/icons-material/Search";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import {
  resolveAttendancePageState,
  clearAttendanceLandingState,
  isManagerKioskPageState,
  getAttendanceOrganizationsForUser,
  getManagerAttendanceOrganizations,
  isManagerAttendanceMode,
  persistAttendanceLandingState,
  resolveKioskSoundEnabledAsync,
  resolveKioskSoundEnabled,
} from "../../Utils/attendanceAuth";
import {
  playAttendanceSuccessSoundIfEnabled,
  unlockAttendanceAudio,
} from "../../Utils/attendanceSound";
import { clearClientPreferredOrganizationId } from "../../Utils/clientActiveOrganization";
import Swal from "sweetalert2";
import axios from "axios";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOutIcon } from "lucide-react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WorkTimeClock from "../../Components/WorkTimeClock/WorkTimeClock";
import { format } from "date-fns";

const StyledContainer = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : "#ffffff",
  minHeight: "100vh",
  padding: theme.spacing(1.25, 2, 2, 2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(1.5),
  color: "text.primary",
}));

const CircleContainer = styled(Box)(({ theme }) => ({
  width: 168,
  height: 168,
  border: "2px solid #4caf50",
  borderRadius: "50%",
  margin: theme.spacing(1.5, 0, 1, 0),
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : "#ffffff",
  color: "text.primary",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  overflow: "hidden",
  position: "relative",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : "#ffffff",
  },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.25),
  flexWrap: "wrap",
  justifyContent: "center",
  marginTop: theme.spacing(1.25),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  minWidth: 98,
  minHeight: 62,
  border: "1px solid #4caf50",
  borderRadius: theme.spacing(1.2),
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : "#ffffff",
  color: "#0f172a",
  fontSize: "13px",
  fontWeight: 500,
  textTransform: "none",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  lineHeight: 1.12,
  padding: theme.spacing(0.65, 1),
  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#f6fff8",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
    borderColor: "#3ea44a",
  },
  "&.Mui-disabled": {
    opacity: 0.7,
    color: "#96a0ad",
    borderColor: "#d0d7de",
    backgroundColor: "#f7f7f7",
    boxShadow: "none",
  },
}));

const ChangePhotoButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: "10px",
  minHeight: "24px",
  padding: theme.spacing(0.5, 1),
  backgroundColor: "#4caf50",
  color: "white",
  textTransform: "none",
  borderRadius: theme.spacing(1),
  "&:hover": {
    backgroundColor: "#45a049",
  },
}));

const ImageContainer = styled(Box)({
  width: "100%",
  height: "100%",
  position: "relative",
  "&:hover .edit-overlay": {
    opacity: 1,
  },
});

const SearchContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 400,
  marginBottom: theme.spacing(2),
  position: "relative",
  color: "text.primary",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : "#ffffff",
    borderRadius: theme.spacing(1),
    "& fieldset": {
      borderColor: "#4caf50",
    },
    "&:hover fieldset": {
      borderColor: "#45a049",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4caf50",
    },
  },
  "& .MuiInputLabel-root": {
    color: "text.primary",
    "&.Mui-focused": {
      color: "text.primary",
    },
  },
}));

const SearchResultsContainer = styled(Paper)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 1000,
  maxHeight: 200,
  overflowY: "auto",
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : "#ffffff",
  color: "text.primary",
  border: "1px solid #4caf50",
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(0.5),
}));

const SearchResultItem = styled(ListItem)(({ theme }) => ({
  cursor: "pointer",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : "#ffffff",
  },
  borderBottom: "1px solid #e0e0e0",
  "&:last-child": {
    borderBottom: "none",
  },
}));

const FaceAuth = () => {
  const location = useLocation();
  const { userData, resetAuth } = useAuthStore();
  const org = userData?.organization;
  const item = resolveAttendancePageState(userData, location.state);
  const isKioskMode = isManagerKioskPageState(item, userData);
  const clientOrganizations = isManagerAttendanceMode(userData)
    ? getManagerAttendanceOrganizations(userData)
    : getAttendanceOrganizationsForUser(userData);
  const showChangeOrganization =
    isKioskMode && clientOrganizations.length > 1;

  const attendanceOrganizationId =
    item?.organization_id ?? org?.organization_id ?? null;

  const maybeUnlockAttendanceAudio = () => {
    if (resolveKioskSoundEnabled(userData, attendanceOrganizationId)) {
      unlockAttendanceAudio();
    }
  };

  useEffect(() => {
    if (!attendanceOrganizationId) return;

    void resolveKioskSoundEnabledAsync(userData, attendanceOrganizationId);
  }, [userData, attendanceOrganizationId]);

  const { logout } = useAuthStore();

  const organizationPersonIdFromContext = item?.organization_person_id || item?.employee_id;

  const [hasPreselectedEmployee, setHasPreselectedEmployee] = useState(
    !isKioskMode &&
      Boolean(item?.organization_person_id || item?.employee_id)
  );

  useEffect(() => {
    if (isKioskMode) {
      setHasPreselectedEmployee(false);
      return;
    }
    if (item?.organization_person_id || item?.employee_id) {
      setHasPreselectedEmployee(true);
    }
  }, [item?.organization_person_id, item?.employee_id, isKioskMode]);
  const fetchAbortControllerRef = useRef(null);

  console.log("item is  ", item);
  console.log("  Current userData:", userData);
  console.log("  Organization data:", org);
  console.log("  Organization name:", item?.organization_name);

  const [currentDateTime, setCurrentDateTime] = useState("");
  const [isCapturing, setIsCapturing] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [permissionState, setPermissionState] = useState("prompt");
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [faceCaptureStatus, setFaceCaptureStatus] = useState("");
  const [challengeInstruction, setChallengeInstruction] = useState("");
  const [challengeStepProgress, setChallengeStepProgress] = useState("");
  const navigate = useNavigate();
  const [userData2, setUserData2] = useState({
    name: "Name",
    designation: "Designation",
    functionalRole: "Functional Role",
    authorized: false,
    organization_person_id: null,
    employee_id: null,
    intern_id: null,
    profileImage: "Profile_Image_Url",
    currentTotal: null,
  });
  const [identifiedPersonType, setIdentifiedPersonType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [organizationData, setOrganizationData] = useState({
    organization_id: item?.organization_id,
    name: item?.organization_name,
  });
  const [clockInTime, setClockInTime] = useState(null);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [isBreakStarted, setIsBreakStarted] = useState(false);
  const [breakEndTime, setBreakEndTime] = useState(null);
  const [isBreakEnded, setIsBreakEnded] = useState(false);
  const [clockOutTime, setClockOutTime] = useState(null);
  const [isClockedOut, setIsClockedOut] = useState(false);
  const [showAttendanceRecord, setShowAttendanceRecord] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");


const [buttonsDisabled, setButtonsDisabled] = useState(false);

  const [allowed, setAllowed] = useState({
    clockin: true,
    breakstart: false,
    breakend: false,
    clockout: false,
  });

  const getLogTimeByType = (logs = [], logType) => {
    if (!Array.isArray(logs)) return null;
    const matchedLog = logs
      .filter((entry) => entry?.attendance_log_type === logType)
      .sort(
        (a, b) =>
          new Date(b?.attendance_log_time || b?.created_at || 0).getTime() -
          new Date(a?.attendance_log_time || a?.created_at || 0).getTime()
      )[0];

    return matchedLog?.attendance_log_time || matchedLog?.created_at || null;
  };

  const formatButtonTime = (timeValue) => {
    if (!timeValue) return null;
    const parsed = new Date(timeValue);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed
      .toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      })
      .toUpperCase();
  };

  const attendanceLogs = userData2?.Today_Attendance || [];
  const clockInDisplayTime = formatButtonTime(
    clockInTime || getLogTimeByType(attendanceLogs, "Clock In")
  );
  const breakStartDisplayTime = formatButtonTime(
    breakStartTime || getLogTimeByType(attendanceLogs, "Break Start")
  );
  const breakEndDisplayTime = formatButtonTime(
    breakEndTime || getLogTimeByType(attendanceLogs, "Break End")
  );
  const clockOutDisplayTime = formatButtonTime(
    clockOutTime || getLogTimeByType(attendanceLogs, "Clock Out")
  );



  const resetAttendanceStates = () => {
    setClockInTime(null);
    setBreakStartTime(null);
    setBreakEndTime(null);
    setClockOutTime(null);
    setShowAttendanceRecord(false);
    setSelectedEmployee(null);
  };

  const completeAttendanceAction = async (message) => {
    const soundEnabled = await resolveKioskSoundEnabledAsync(
      userData,
      attendanceOrganizationId
    );
    playAttendanceSuccessSoundIfEnabled(soundEnabled);

    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);

    if (isKioskMode) {
      setTimeout(() => {
        resetSelectedEmployee();
      }, 2000);
      return;
    }

    resetAttendanceStates();
    window.location.reload();
  };

  const resetSelectedEmployee = () => {
    setUserData2({
      name: "Name",
      designation: "Designation",
      functionalRole: "Functional Role",
      authorized: false,
      organization_person_id: null,
      employee_id: null,
      intern_id: null,
      profileImage: "Profile_Image_Url",
      currentTotal: null,
      TotalBreaks: null,
      TotalAttendance: null,
      Today_Attendance: null,
      Should_Stop_BreakCal: null,
      Stop_Cal_Work: null,
    });
    setIdentifiedPersonType(null);
    setHasPreselectedEmployee(false);
    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
    setAllowed({
      clockin: true,
      breakstart: false,
      breakend: false,
      clockout: false,
    });
    setButtonsDisabled(false);
    setCameraError("");
    setFaceCaptureStatus("");
    resetAttendanceStates();
  };

  const handleNextEmployee = () => {
    resetSelectedEmployee();
  };

  const handleInternSelect = (intern) => {
    const internName = intern.Name || intern.name;
    const internFunctionalRole =
      intern.Functional_Role || intern.functionalRole || "Intern";
    const organizationPersonId = intern.id || intern.organization_person_id;
    const internId = intern.intern_id || intern.Intern_ID;
    const profileImageUrl =
      intern.profile_image_url ||
      intern.Profile_Image_Url ||
      intern.profileImage;
    const currentTotal = intern.Total_Worked_Time;
    const TotalAttendance = intern?.Today_Attendance;
    const TotalBreaks = intern?.Total_Breaks;
    const Should_Stop_BreakCal = intern?.Should_Stop_BreakCal;
    const Stop_Cal_Work = intern?.Stop_Cal_Work;

    setIdentifiedPersonType("intern");
    setUserData2({
      name: internName,
      designation: "Designation",
      functionalRole: internFunctionalRole,
      authorized: true,
      organization_person_id: organizationPersonId,
      employee_id: null,
      intern_id: internId,
      profileImage: profileImageUrl,
      currentTotal: currentTotal,
      TotalAttendance: TotalAttendance,
      Today_Attendance: TotalAttendance,
      TotalBreaks: TotalBreaks,
      Should_Stop_BreakCal: Should_Stop_BreakCal,
      Stop_Cal_Work: Stop_Cal_Work,
    });
    setHasPreselectedEmployee(true);
    setSearchQuery(internName);
    setShowResults(false);
    setSearchResults([]);
    setAllowed({
      clockin: Boolean(intern.clockin ?? intern.allowed?.clockin ?? true),
      breakstart: Boolean(
        intern.breakstart ?? intern.allowed?.breakstart ?? false
      ),
      breakend: Boolean(intern.breakend ?? intern.allowed?.breakend ?? false),
      clockout: Boolean(intern.clockout ?? intern.allowed?.clockout ?? false),
    });
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");

          await axios.get(`${MAIN_URL}/api/auth/logout`, {
            headers: getApiHeaders(),
          });
          logout();
          clearClientPreferredOrganizationId();
          clearAttendanceLandingState();
          localStorage.removeItem("token");
          Swal.fire("Logged out!", "You have been logged out.", "success");
          resetAuth();
          navigate("/login");
         
        } catch (error) {
          console.error("Logout failed:", error);
          logout();
          clearClientPreferredOrganizationId();
          clearAttendanceLandingState();
          localStorage.removeItem("token");
          navigate("/login");
         
        }
      }
    });
  };

  const handleBack = () => {
    try {
      if (isKioskMode || showChangeOrganization) {
        clearAttendanceLandingState();
        navigate("/organization-toggle", { replace: true });
        return;
      }
      navigate(-1);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        navigate("/login");
       
        return;
      }

      console.error("Navigation failed:", error);
    }
  };

  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const pendingActionRef = useRef(null);

  const theme = useTheme();

  const [apiUrl, setApiUrl] = useState(
    `${MAIN_URL}/api/organizations/${item?.organization_id}/filter-employee`
  );

  const checkCameraPermission = async () => {
    try {
      const result = await navigator.permissions.query({ name: "camera" });
      setPermissionState(result.state);
      return result.state;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        navigate("/login");
       
        return;
      }

      console.log(
        "  Permission API not supported, proceeding with getUserMedia"
      );
      return "prompt";
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
    setIsCapturing(false);
  };

  useEffect(() => {
    if (isCameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [isCameraActive]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const captureSingleFrameBlob = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      throw new Error("Camera not ready");
    }

    canvas.width = video.videoWidth || 320;
    canvas.height = video.videoHeight || 240;
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0);

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) resolve(result);
          else reject(new Error("Failed to capture image"));
        },
        "image/jpeg",
        0.85
      );
    });
  };

  const getFaceVerificationSubjectId = () =>
    userData2?.employee_id || userData2?.intern_id;

  const hasSelectedPersonForAttendance = () =>
    Boolean(
      userData2?.organization_person_id ||
        userData2?.employee_id ||
        userData2?.intern_id
    );

  const performFaceIdentification = async (imageBlob) => {
    const organizationId = item?.organization_id || org?.organization_id;
    if (!organizationId) {
      throw new Error("Organization context is missing.");
    }

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication token is missing. Please login again.");
    }

    const formData = new FormData();
    formData.append("image", imageBlob, "face.jpg");
    formData.append("organization_id", organizationId.toString());

    const response = await fetch(`${MAIN_URL}/api/face/identify`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok || !result.success || !result.match) {
      throw new Error(result.message || "Face not recognized. Please try again.");
    }

    return result;
  };

  const loadEmployeeAfterIdentify = async (identifyResult) => {
    setIdentifiedPersonType("employee");
    const token = localStorage.getItem("token");
    const organizationId = item?.organization_id || org?.organization_id;

    const employee = await fetchFilterEmployeeStatus({
      MAIN_URL,
      organizationId,
      token,
      organizationPersonId: identifyResult.organization_person_id,
      employeeId: identifyResult.employee_id,
    });

    if (employee) {
      handleEmployeeSelect(employee);
      return;
    }

    setUserData2({
      name: identifyResult.name || "Name",
      designation: identifyResult.designation || "Designation",
      functionalRole: "Functional Role",
      authorized: true,
      organization_person_id: identifyResult.organization_person_id,
      employee_id: identifyResult.employee_id,
      intern_id: null,
      profileImage: identifyResult.profile_image_url || "Profile_Image_Url",
      currentTotal: null,
      TotalBreaks: null,
      TotalAttendance: null,
      Today_Attendance: null,
      Should_Stop_BreakCal: null,
      Stop_Cal_Work: null,
    });
    setHasPreselectedEmployee(true);
    setAllowed({
      clockin: true,
      breakstart: false,
      breakend: false,
      clockout: false,
    });
  };

  const loadInternAfterIdentify = async (identifyResult) => {
    const token = localStorage.getItem("token");
    const organizationId = item?.organization_id || org?.organization_id;

    const intern = await fetchFilterInternStatus({
      MAIN_URL,
      organizationId,
      token,
      organizationPersonId: identifyResult.organization_person_id,
      internId: identifyResult.intern_id,
    });

    if (intern) {
      handleInternSelect(intern);
      return;
    }

    setIdentifiedPersonType("intern");
    setUserData2({
      name: identifyResult.name || "Name",
      designation: "Designation",
      functionalRole: identifyResult.functional_role || "Intern",
      authorized: true,
      organization_person_id: identifyResult.organization_person_id,
      employee_id: null,
      intern_id: identifyResult.intern_id,
      profileImage: identifyResult.profile_image_url || "Profile_Image_Url",
      currentTotal: null,
      TotalBreaks: null,
      TotalAttendance: null,
      Today_Attendance: null,
      Should_Stop_BreakCal: null,
      Stop_Cal_Work: null,
    });
    setHasPreselectedEmployee(true);
    setAllowed({
      clockin: true,
      breakstart: false,
      breakend: false,
      clockout: false,
    });
  };

  const loadPersonAfterIdentify = async (identifyResult) => {
    if (identifyResult.person_type === "intern") {
      await loadInternAfterIdentify(identifyResult);
      return;
    }
    await loadEmployeeAfterIdentify(identifyResult);
  };

  const startFaceIdentification = async () => {
    if (!isKioskMode || isLoading || isCameraActive) return;

    maybeUnlockAttendanceAudio();

    setButtonsDisabled(true);
    setIsLoading(true);
    setIsCapturing(true);
    setCameraError("");
    setFaceCaptureStatus("opening");

    try {
      const permissionStateResult = await checkCameraPermission();
      if (permissionStateResult === "denied") {
        setCameraError(
          "Camera access denied. Please enable camera permissions in your browser settings."
        );
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 480 },
          height: { ideal: 480 },
          facingMode: "user",
        },
      });

      streamRef.current = stream;
      setIsCameraActive(true);
      setFaceCaptureStatus("capturing");
      setPermissionState("granted");

      await new Promise((resolve) => setTimeout(resolve, 1200));

      const blob = await captureSingleFrameBlob();
      stopCamera();
      setFaceCaptureStatus("verifying");

      const identifyResult = await performFaceIdentification(blob);
      await loadPersonAfterIdentify(identifyResult);
      setFaceCaptureStatus("verified");
    } catch (error) {
      stopCamera();
      setFaceCaptureStatus("failed");

      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        navigate("/login");
        return;
      }

      let errorMessage =
        error.message || "Face identification failed. Please try again.";
      if (error.name === "NotAllowedError") {
        errorMessage =
          "Camera access denied. Please allow camera permissions and try again.";
        setPermissionState("denied");
      } else if (error.name === "NotFoundError") {
        errorMessage =
          "No camera found. Please ensure your device has a camera connected.";
      }

      setCameraError(errorMessage);
    } finally {
      setIsLoading(false);
      setIsCapturing(false);
      setButtonsDisabled(false);
      setTimeout(() => setFaceCaptureStatus(""), 2500);
    }
  };

  const handlePhotoClick = () => {
    if (!isKioskMode || isLoading || isCameraActive) return;
    if (userData2?.name !== "Name") return;
    startFaceIdentification();
  };

  const performFaceRecognition = async (imageBlob, attendanceAction) => {
    const organizationId = item?.organization_id || org?.organization_id;
    const subjectId = getFaceVerificationSubjectId();

    if (!hasSelectedPersonForAttendance()) {
      setCameraError("Please scan face before marking attendance.");
      return false;
    }

    if (!subjectId) {
      setCameraError("Person ID is missing. Please scan face again.");
      return false;
    }

    if (!organizationId) {
      setCameraError("Organization context is missing.");
      return false;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setCameraError("Authentication token is missing. Please login again.");
      return false;
    }

    const formData = new FormData();
    if (!imageBlob) {
      throw new Error("Unable to capture final face image.");
    }

    formData.append("image", imageBlob, "face.jpg");
    formData.append("organization_id", organizationId.toString());
    formData.append("employee_id", String(subjectId));
    if (userData2.organization_person_id) {
      formData.append(
        "organization_person_id",
        String(userData2.organization_person_id)
      );
    }
    if (attendanceAction) {
      formData.append("attendance_action", attendanceAction);
    }

    const response = await fetch(`${MAIN_URL}/api/face/recognize`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        result.message || `Face recognition failed (${response.status})`
      );
    }

    if (result.success && result.match) {
      setUserData2((prev) => ({
        ...prev,
        authorized: true,
        name: result.name || prev.name,
        designation: result.designation || prev.designation,
        functionalRole: result.functional_role || prev.functionalRole,
        employee_id: result.employee_id || prev.employee_id,
        intern_id: result.intern_id || prev.intern_id,
        organization_person_id:
          result.organization_person_id ||
          result.employee?.organization_person_id ||
          result.intern?.organization_person_id ||
          prev.organization_person_id,
      }));
      setCameraError("");
      return true;
    }

    setCameraError(result.message || "Face not recognized. Please try again.");
    return false;
  };

  const runAttendanceAction = async (action) => {
    if (identifiedPersonType === "intern") {
      if (action === "clockin") await executeInternClockIn();
      else if (action === "clockout") await executeInternClockOut();
      else if (action === "breakstart") await executeInternBreakStart();
      else if (action === "breakend") await executeInternBreakEnd();
      return;
    }

    if (action === "clockin") await executeClockIn();
    else if (action === "clockout") await executeClockOut();
    else if (action === "breakstart") await executeBreakStart();
    else if (action === "breakend") await executeBreakEnd();
  };

  const startFaceVerificationAndAction = async (action) => {
    if (isLoading || isCameraActive) return;

    maybeUnlockAttendanceAudio();

    if (!hasSelectedPersonForAttendance()) {
      setCameraError(
        isKioskMode
          ? "Please tap the photo to scan face first."
          : "Please select a person first."
      );
      return;
    }

    if (!getFaceVerificationSubjectId()) {
      setCameraError("Person ID is missing. Please scan face again.");
      return;
    }

    // Manager kiosk: person is already identified — no re-scan on action buttons.
    if (isKioskMode) {
      setButtonsDisabled(true);
      setIsLoading(true);
      setCameraError("");
      try {
        await runAttendanceAction(action);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          toast.error("Session Expired!");
          navigate("/login");
          return;
        }
        console.error("Attendance action failed:", error);
        setCameraError(
          error.message || "Failed to mark attendance. Please try again."
        );
      } finally {
        setIsLoading(false);
        setButtonsDisabled(false);
      }
      return;
    }

    pendingActionRef.current = action;
    setButtonsDisabled(true);
    setIsLoading(true);
    setIsCapturing(true);
    setCameraError("");
    setFaceCaptureStatus("opening");

    try {
      const permissionStateResult = await checkCameraPermission();
      if (permissionStateResult === "denied") {
        setCameraError(
          "Camera access denied. Please enable camera permissions in your browser settings."
        );
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 480 },
          height: { ideal: 480 },
          facingMode: "user",
        },
      });

      streamRef.current = stream;
      setIsCameraActive(true);
      setFaceCaptureStatus("capturing");
      setPermissionState("granted");

      await new Promise((resolve) => setTimeout(resolve, 1200));

      await new Promise((resolve) => setTimeout(resolve, 800));
      const imageBlob = await captureSingleFrameBlob();
      stopCamera();
      setFaceCaptureStatus("verifying");

      const matched = await performFaceRecognition(imageBlob, action);
      if (!matched) {
        setFaceCaptureStatus("failed");
        return;
      }
      setFaceCaptureStatus("verified");

      await runAttendanceAction(action);
    } catch (error) {
      stopCamera();
      setFaceCaptureStatus("failed");

      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        navigate("/login");
        return;
      }

      console.error("Face verification failed:", error);

      let errorMessage = error.message || "Face verification failed. Please try again.";
      if (error.name === "NotAllowedError") {
        errorMessage =
          "Camera access denied. Please allow camera permissions and try again.";
        setPermissionState("denied");
      } else if (error.name === "NotFoundError") {
        errorMessage =
          "No camera found. Please ensure your device has a camera connected.";
      }

      setCameraError(errorMessage);
    } finally {
      pendingActionRef.current = null;
      setChallengeInstruction("");
      setChallengeStepProgress("");
      setIsLoading(false);
      setIsCapturing(false);
      setButtonsDisabled(false);
      setTimeout(() => setFaceCaptureStatus(""), 2500);
    }
  };

  const searchEmployees = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    if (!apiUrl) {
      console.error("  API URL not available yet");
      return;
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        mode: "cors",
      });

      console.log("  Search response status:", response.status);

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log("  Raw search API response:", data);

      let employees = Array.isArray(data)
        ? data
        : data.employees || data.data || [];

      if (
        Array.isArray(employees) &&
        employees.length > 0 &&
        Array.isArray(employees[0])
      ) {
        employees = employees[0];
      }

      console.log("  Total employees from API:", employees.length);

      const filteredEmployees = employees.filter((employee) => {
        const employeeName = (employee.name || "").toLowerCase();
        const employeeDesignation = (
          employee.Designation ||
          employee.designation ||
          ""
        ).toLowerCase();

        const employeeCode = (
          employee.Employee_Code ||
          employee.employee_code ||
          employee.employeeCode ||
          employee.code ||
          ""
        )
          .toString()
          .toLowerCase();
        const searchTerm = query.toLowerCase().trim();

        console.log("  Checking employee:", {
          id: employee.id || employee.employee_id,
          name: employeeName,
          designation: employeeDesignation,
          employeeCode: employeeCode,
          originalEmployeeCode: employee.Employee_Code,
          searchTerm: searchTerm,
        });

        const nameMatch = employeeName.includes(searchTerm);
        const designationMatch = employeeDesignation.includes(searchTerm);
        const codeMatch = employeeCode.includes(searchTerm);

        const isMatch = nameMatch || designationMatch || codeMatch;

        if (isMatch) {
          console.log("  MATCH FOUND:", {
            employee: employee.Name || employee.name,
            code: employee.Employee_Code,
            matchType: nameMatch
              ? "name"
              : designationMatch
                ? "designation"
                : "code",
          });
        }

        return isMatch;
      });

      console.log("  Filtered employees count:", filteredEmployees.length);
      console.log("  Search query was:", query);

      setSearchResults(filteredEmployees);
      setShowResults(true);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        navigate("/login");
       
        return;
      }

      console.error("  Detailed search error:", error);
      setSearchResults([]);
      setShowResults(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);

    const timeoutId = setTimeout(() => {
      searchEmployees(value);
    }, 300);

    if (handleSearchChange.timeoutId) {
      clearTimeout(handleSearchChange.timeoutId);
    }
    handleSearchChange.timeoutId = timeoutId;
  };

  console.log("userdata2  is", userData2);

  const handleEmployeeSelect = (employee) => {
    const employeeName = employee.Name || employee.name;
    const employeeDesignation =
      employee.Designation || employee.designation || "Employee";
    const organizationPersonId =
      employee.organization_person_id || employee.id || employee.employee_id;
    const employeeId =
      employee.employee_id || employee.Employee_ID || employee.employeeId;
    const profileImageUrl =
      employee.Profile_Image_Url ||
      employee.profile_image_url ||
      employee.profileImage;
    const currentTotal = employee.Total_Worked_Time;
    const TotalAttendance = employee?.Today_Attendance || employee?.TotalAttendance;
    const TotalBreaks = employee?.Total_Breaks,
      Should_Stop_BreakCal = employee?.Should_Stop_BreakCal,
      Stop_Cal_Work = employee?.Stop_Cal_Work;

    console.log("Selected employee:", employee);
    console.log("Extracted name:", employeeName);
    console.log("Extracted designation:", employeeDesignation);
    console.log("Extracted organization_person_id:", organizationPersonId);
    console.log("Extracted profile image:", profileImageUrl);

    setUserData2({
      name: employeeName,
      designation: employeeDesignation,
      functionalRole: "Functional Role",
      authorized: false,
      organization_person_id: organizationPersonId,
      employee_id: employeeId,
      intern_id: null,
      profileImage: profileImageUrl,
      currentTotal: currentTotal,
      TotalAttendance: TotalAttendance,
      Today_Attendance: TotalAttendance,
      TotalBreaks: TotalBreaks,
      Should_Stop_BreakCal: Should_Stop_BreakCal,
      Stop_Cal_Work: Stop_Cal_Work,
    });
    setSearchQuery(employeeName);
    setShowResults(false);
    setSearchResults([]);

    if (isKioskMode) {
      setIdentifiedPersonType("employee");
    }

    setAllowed({
      clockin: Boolean(employee.clockin ?? employee.allowed?.clockin ?? true),
      breakstart: Boolean(
        employee.breakstart ?? employee.allowed?.breakstart ?? false
      ),
      breakend: Boolean(
        employee.breakend ?? employee.allowed?.breakend ?? false
      ),
      clockout: Boolean(
        employee.clockout ?? employee.allowed?.clockout ?? false
      ),
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".search-container")) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const refreshEmployeeStatus = async () => {
    try {
      if (!item?.organization_id) return;
      if (
        !userData2?.organization_person_id &&
        !userData2?.employee_id
      )
        return;

      const token = localStorage.getItem("token");
      if (!token) return;

      // Only use organization_person_id or employee_id, not name to avoid matching wrong employee
      const employee = await fetchFilterEmployeeStatus({
        MAIN_URL,
        organizationId: item?.organization_id,
        token,
        organizationPersonId: userData2.organization_person_id || userData2.employee_id,
        employeeId: userData2.employee_id,
      });

      const employeeOrgPersonId = employee.id || employee.organization_person_id;
      const employeeIdVal = employee.employee_id || employee.Employee_ID || employee.id;
      if (employee && (
        employeeOrgPersonId === userData2.organization_person_id ||
        employeeOrgPersonId === userData2.employee_id ||
        employeeIdVal === userData2.employee_id ||
        employeeIdVal === userData2.organization_person_id
      )) {
        setIsClockedIn(Boolean(employee?.clockin));
        setIsBreakStarted(Boolean(employee?.breakstart));
        setIsBreakEnded(Boolean(employee?.breakend));
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        toast.error("Session Expired!");
        navigate("/login");
       
        return;
      }

      console.log("[v0] refreshEmployeeStatus error:", err);
    }
  };

  const refreshInternStatus = async () => {
    try {
      if (!item?.organization_id) return;
      if (!userData2?.organization_person_id && !userData2?.intern_id) return;

      const token = localStorage.getItem("token");
      if (!token) return;

      const intern = await fetchFilterInternStatus({
        MAIN_URL,
        organizationId: item?.organization_id,
        token,
        organizationPersonId:
          userData2.organization_person_id || userData2.intern_id,
        internId: userData2.intern_id,
      });

      const internOrgPersonId = intern?.id || intern?.organization_person_id;
      const internIdVal = intern?.intern_id || intern?.Intern_ID || intern?.id;
      if (
        intern &&
        (internOrgPersonId === userData2.organization_person_id ||
          internOrgPersonId === userData2.intern_id ||
          internIdVal === userData2.intern_id ||
          internIdVal === userData2.organization_person_id)
      ) {
        setIsClockedIn(Boolean(intern?.clockin));
        setIsBreakStarted(Boolean(intern?.breakstart));
        setIsBreakEnded(Boolean(intern?.breakend));
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        toast.error("Session Expired!");
        navigate("/login");
        return;
      }

      console.log("[v0] refreshInternStatus error:", err);
    }
  };

  useEffect(() => {
    if (!userData2.organization_person_id && !userData2.employee_id && !userData2.intern_id) {
      return;
    }

    if (isKioskIntern) {
      refreshInternStatus();
      return;
    }

    if (userData2.organization_person_id || userData2.employee_id) {
      refreshEmployeeStatus();
    }
  }, [
    userData2.organization_person_id,
    userData2.employee_id,
    userData2.intern_id,
    identifiedPersonType,
    item?.organization_id,
  ]);




  const postInternTimeLog = async (logType) => {
    const organizationPersonId =
      userData2.organization_person_id || userData2.intern_id;
    if (!organizationPersonId) {
      throw new Error("Organization Person ID is missing.");
    }

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication token is missing. Please login again.");
    }

    const now = new Date();
    const timestamp = now.toISOString();
    const formData = new FormData();
    formData.append("organization_person_id", String(organizationPersonId));
    formData.append("attendance_log_type", logType);
    formData.append("timestamp", timestamp);
    formData.append("date", now.toISOString().split("T")[0]);
    formData.append(
      "remarks",
      ` Created By ${userData?.full_name} --  Application user Id is ${userData?.application_user_id} -- (created on ${format(new Date(), "yyyy-MM-dd HH:mm:ss")})`
    );

    const apiEndpoint = `${MAIN_URL}/api/organizations/${item?.organization_id}/intern-time-logs`;
    const response = await fetch(apiEndpoint, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`${logType} failed: ${response.status} ${text}`);
    }

    await response.json().catch(() => null);
    return timestamp;
  };

  const executeInternClockIn = async () => {
    const timestamp = await postInternTimeLog("Clock In");
    setClockInTime(timestamp);
    setIsClockedIn(true);
    setAllowed({
      clockin: false,
      breakstart: true,
      breakend: false,
      clockout: true,
    });
    await refreshInternStatus();
    await completeAttendanceAction("Intern Login Successfully");
  };

  const executeInternClockOut = async () => {
    const timestamp = await postInternTimeLog("Clock Out");
    setIsClockedOut(true);
    setClockOutTime(timestamp);
    setAllowed({
      clockin: true,
      breakstart: false,
      breakend: false,
      clockout: false,
    });
    await refreshInternStatus();
    await completeAttendanceAction("Intern Clocked Out Successfully");
  };

  const executeInternBreakStart = async () => {
    const timestamp = await postInternTimeLog("Break Start");
    setIsBreakStarted(true);
    setBreakStartTime(timestamp);
    setAllowed({
      clockin: false,
      breakstart: false,
      breakend: true,
      clockout: true,
    });
    await refreshInternStatus();
    await completeAttendanceAction("Break Started Successfully");
  };

  const executeInternBreakEnd = async () => {
    const timestamp = await postInternTimeLog("Break End");
    setIsBreakEnded(true);
    setBreakEndTime(timestamp);
    setAllowed({
      clockin: false,
      breakstart: true,
      breakend: false,
      clockout: true,
    });
    await refreshInternStatus();
    await completeAttendanceAction("Break Ended Successfully");
  };

  const executeClockIn = async () => {
    const organizationPersonId =
      userData2.organization_person_id || userData2.employee_id;
    if (!organizationPersonId) {
      alert(
        "Organization Person ID is missing. Please select an employee from the search results."
      );
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      alert("Authentication token is missing. Please login again.");
      return;
    }

    const now = new Date();
    const timestamp = now.toISOString();
    const formData = new FormData();
    formData.append("organization_person_id", organizationPersonId.toString());
    formData.append("attendance_log_type", "Clock In");
    formData.append("timestamp", timestamp);
    formData.append("date", now.toISOString().split("T")[0]);
    formData.append(
      "remarks",
      ` Created By ${userData?.full_name} --  Application user Id is ${userData?.application_user_id} -- (created on ${format(new Date(), "yyyy-MM-dd HH:mm:ss")})`
    );

    const apiEndpoint = `${MAIN_URL}/api/organizations/${item?.organization_id}/attendance-time-logs`;
    const response = await fetch(apiEndpoint, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      console.error("Clock in failed:", response.status, text);
      alert("Clock in failed. Please try again.");
      return;
    }

    await response.json().catch(() => null);

    setClockInTime(timestamp);
    setIsClockedIn(true);
    setSelectedEmployee(userData2);

    setAllowed({
      clockin: false,
      breakstart: true,
      breakend: false,
      clockout: true,
    });
    await refreshEmployeeStatus();
    await completeAttendanceAction("Employee Login Successfully");
  };

  const executeClockOut = async () => {
    const organizationPersonId =
      userData2?.organization_person_id || userData2?.employee_id;
    if (!organizationPersonId) {
      alert("Please select an employee first");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      alert("Authentication token is missing. Please login again.");
      return;
    }

    const now = new Date();
    const timestamp = now.toISOString();
    const formData = new FormData();
    formData.append("organization_person_id", String(organizationPersonId));
    formData.append("attendance_log_type", "Clock Out");
    formData.append("timestamp", timestamp);
    formData.append("date", now.toISOString().split("T")[0]);
    formData.append(
      "remarks",
      ` Created By ${userData?.full_name} --   user Id is ${userData?.application_user_id} -- (created on ${format(new Date(), "yyyy-MM-dd HH:mm:ss")})`
    );

    const apiEndpoint = `${MAIN_URL}/api/organizations/${item?.organization_id}/attendance-time-logs`;
    const res = await fetch(apiEndpoint, {
      method: "POST",
      body: formData,
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`Clock out failed: ${res.status} ${t}`);
    }

    setIsClockedOut(true);
    setClockOutTime(timestamp);

    setAllowed({
      clockin: true,
      breakstart: false,
      breakend: false,
      clockout: false,
    });
    await refreshEmployeeStatus();
    await completeAttendanceAction("Employee Clocked Out Successfully");
  };

  const executeBreakStart = async () => {
    const organizationPersonId =
      userData2?.organization_person_id || userData2?.employee_id;
    if (!organizationPersonId) {
      alert("Please select an employee first");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      alert("Authentication token is missing. Please login again.");
      return;
    }

    const now = new Date();
    const timestamp = now.toISOString();
    const formData = new FormData();
    formData.append("organization_person_id", String(organizationPersonId));
    formData.append("attendance_log_type", "Break Start");
    formData.append("timestamp", timestamp);
    formData.append("date", now.toISOString().split("T")[0]);
    formData.append(
      "remarks",
      ` Created By ${userData?.full_name} --   user Id is ${userData?.application_user_id} -- (created on ${format(new Date(), "yyyy-MM-dd HH:mm:ss")})`
    );

    const apiEndpoint = `${MAIN_URL}/api/organizations/${item?.organization_id}/attendance-time-logs`;
    const res = await fetch(apiEndpoint, {
      method: "POST",
      body: formData,
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`Break start failed: ${res.status} ${t}`);
    }

    setIsBreakStarted(true);
    setBreakStartTime(timestamp);

    setAllowed({
      clockin: false,
      breakstart: false,
      breakend: true,
      clockout: true,
    });
    await refreshEmployeeStatus();
    await completeAttendanceAction("Break Started Successfully");
  };

  const executeBreakEnd = async () => {
    const organizationPersonId =
      userData2?.organization_person_id || userData2?.employee_id;
    if (!organizationPersonId) {
      alert("Please select an employee first");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      alert("Authentication token is missing. Please login again.");
      return;
    }

    const now = new Date();
    const timestamp = now.toISOString();
    const formData = new FormData();
    formData.append("organization_person_id", String(organizationPersonId));
    formData.append("attendance_log_type", "Break End");
    formData.append("timestamp", timestamp);
    formData.append("date", now.toISOString().split("T")[0]);
    formData.append(
      "remarks",
      ` Created By ${userData?.full_name} --   user Id is ${userData?.application_user_id} -- (created on ${format(new Date(), "yyyy-MM-dd HH:mm:ss")})`
    );

    const apiEndpoint = `${MAIN_URL}/api/organizations/${item?.organization_id}/attendance-time-logs`;
    const res = await fetch(apiEndpoint, {
      method: "POST",
      body: formData,
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`Break end failed: ${res.status} ${t}`);
    }

    setIsBreakEnded(true);
    setBreakEndTime(timestamp);

    setAllowed({
      clockin: false,
      breakstart: true,
      breakend: false,
      clockout: true,
    });
    await refreshEmployeeStatus();
    await completeAttendanceAction("Break Ended Successfully");
  };

  const handleClockIn = async () => {
    if (!isPersonSelected()) {
      alert(
        isKioskMode
          ? "Please tap the photo to scan face first"
          : "Please select an employee first"
      );
      return;
    }

    await startFaceVerificationAndAction("clockin");
  };

  const handleStartBreak = async () => {
    if (!isPersonSelected()) {
      alert(
        isKioskMode
          ? "Please tap the photo to scan face first"
          : "Please select an employee first"
      );
      return;
    }

    await startFaceVerificationAndAction("breakstart");
  };

  const handleEndBreak = async () => {
    if (!isPersonSelected()) {
      alert(
        isKioskMode
          ? "Please tap the photo to scan face first"
          : "Please select an employee first"
      );
      return;
    }

    await startFaceVerificationAndAction("breakend");
  };

  const handleClockOut = async () => {
    if (!isPersonSelected()) {
      alert(
        isKioskMode
          ? "Please tap the photo to scan face first"
          : "Please select an employee first"
      );
      return;
    }

    await startFaceVerificationAndAction("clockout");
  };

  useEffect(() => {
    const orgId = item?.organization_id || org?.organization_id;
    if (!orgId) return;
    if (isKioskMode) return;

    // Check if we have employee data from location.state
    const organizationPersonId = item?.organization_person_id || item?.employee_id || organizationPersonIdFromContext;
    
    console.log("[FaceAuth] Loading employee data:", {
      organizationPersonId,
      itemOrganizationPersonId: item?.organization_person_id,
      itemEmployeeId: item?.employee_id,
      hasEmployeeDetails: !!item?.employee_details,
      orgId
    });
    
    if (organizationPersonId) {
      // Cancel any previous fetch
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
      
      const abortController = new AbortController();
      fetchAbortControllerRef.current = abortController;
      
      const token = localStorage.getItem("token");
      if (!token) return;
      
      (async () => {
        try {
          const employee = await fetchFilterEmployeeStatus({
            MAIN_URL,
            organizationId: orgId,
            token,
            organizationPersonId: organizationPersonId,
            employeeId: item?.employee_id || organizationPersonIdFromContext,
          });

          // Check if fetch was aborted
          if (abortController.signal.aborted) return;

          if (employee) {
            // The API returns 'id' field which IS the organization_person_id
            // Verify the employee matches the organization_person_id we're looking for
            const employeeOrgPersonId = employee.id || employee.organization_person_id;
            const targetId = String(organizationPersonId);
            const employeeIdStr = String(employeeOrgPersonId || '');
            const itemOrgPersonId = String(item?.organization_person_id || '');
            
            console.log("[FaceAuth] Employee data received:", {
              targetId,
              employeeIdStr,
              itemOrgPersonId,
              employeeName: employee.Name || employee.name,
              employeeId: employee.id,
              employeeCode: employee.Employee_Code
            });
            
            // Check if employee.id (organization_person_id) matches the target
            const idMatches = employeeIdStr === targetId || 
                             employeeIdStr === itemOrgPersonId ||
                             (itemOrgPersonId && itemOrgPersonId === targetId && employeeIdStr === itemOrgPersonId);
            
            // If we have a specific organization_person_id from login, verify it matches
            if (item?.organization_person_id && !idMatches) {
              console.warn("[FaceAuth] Employee ID mismatch - rejecting:", {
                expected: targetId,
                got: employeeIdStr,
                itemOrgPersonId: itemOrgPersonId,
                employee: employee.Name || employee.name,
                employeeId: employee.id
              });
              return; // Don't use this employee if ID doesn't match
            }
            
            if (idMatches) {
              console.log("[FaceAuth] Employee ID matches - using employee:", {
                targetId,
                employeeIdStr,
                employeeName: employee.Name || employee.name
              });
            }

            const employeeName =
              employee.Name || employee.name || "Unknown Employee";
            const employeeDesignation =
              employee.Designation || employee.designation || "Employee";
            const profileImageUrl =
              employee.Profile_Image_Url ||
              employee.profile_image_url ||
              employee.profileImage ||
              "";
            // The 'id' field IS the organization_person_id in the API response
            const finalOrganizationPersonId = employee.id || employee.organization_person_id || item?.organization_person_id || organizationPersonIdFromContext;
            const finalEmployeeId = employee.employee_id || employee.Employee_ID || item?.employee_id || organizationPersonIdFromContext;

            const currentTotal = employee?.Total_Worked_Time;
            const TotalAttendance =
              employee?.Today_Attendance || employee?.TotalAttendance;
            const TotalBreaks = employee?.Total_Breaks;
            const Should_Stop_BreakCal = employee?.Should_Stop_BreakCal;
            const Stop_Cal_Work = employee?.Stop_Cal_Work;

            setUserData2({
              name: employeeName,
              designation: employeeDesignation,
              authorized: false,
              organization_person_id: finalOrganizationPersonId,
              employee_id: finalEmployeeId, // Keep for backward compatibility
              profileImage: profileImageUrl,
              currentTotal: currentTotal,
              TotalAttendance: TotalAttendance,
              Today_Attendance: TotalAttendance,
              TotalBreaks: TotalBreaks,
              Should_Stop_BreakCal: Should_Stop_BreakCal,
              Stop_Cal_Work: Stop_Cal_Work,
            });
            setHasPreselectedEmployee(true);
            setShowResults(false);

            // Set search query to employee name for display
            setSearchQuery(employeeName);

            // Set initial allowed states based on employee status
            setAllowed({
              clockin: Boolean(employee.clockin ?? true),
              breakstart: Boolean(employee.breakstart ?? false),
              breakend: Boolean(employee.breakend ?? false),
              clockout: Boolean(employee.clockout ?? false),
            });
          }
        } catch (e) {
          if (e.name === 'AbortError') {
            console.log("[v0] Fetch aborted");
            return;
          }
          console.log("[v0] auto-fetch employee failed:", e);
        }
      })();
      
      // Cleanup function
      return () => {
        abortController.abort();
      };
    }
  }, [
    organizationPersonIdFromContext,
    item?.organization_id,
    item?.organization_person_id,
    item?.employee_id,
    org?.organization_id,
    isKioskMode,
  ]);

  useEffect(() => {
    if (item?.organization_id && isKioskMode) {
      persistAttendanceLandingState(item);
    }
  }, [item, isKioskMode]);

  useEffect(() => {
    if (org?.organization_id) {
      setOrganizationData({
        organization_id: item?.organization_id,
        name: item?.organization_name || "Loading...",
      });
      setApiUrl(
        `${MAIN_URL}/api/organizations/${item?.organization_id}/filter-employee`
      );
    }
  }, [org]);

  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const date = now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const time = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      setCurrentDateTime(`${date} | ${time}`);
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  console.log("users data 2", userData2);

  const isKioskAwaitingScan = isKioskMode && userData2?.name === "Name";
  const isKioskIntern =
    isKioskMode && identifiedPersonType === "intern";

  const getDisplayedRole = () =>
    isKioskIntern ? userData2.functionalRole : userData2.designation;

  const isPersonSelected = () => {
    if (userData2.name === "Name") return false;
    if (isKioskIntern) {
      return userData2.functionalRole !== "Functional Role";
    }
    return userData2.designation !== "Designation";
  };

  const getCircleStatusMessage = () => {
    if (faceCaptureStatus === "opening") {
      return "Opening camera...";
    }
    if (faceCaptureStatus === "capturing" || isCameraActive) {
      if (challengeStepProgress && challengeInstruction) {
        return `${challengeStepProgress} — ${challengeInstruction}`;
      }
      if (challengeInstruction) {
        return challengeInstruction;
      }
      if (challengeStepProgress) {
        return challengeStepProgress;
      }
      return "Follow the live face instructions";
    }
    if (faceCaptureStatus === "verifying") {
      return "Verifying face...";
    }
    if (faceCaptureStatus === "verified") {
      return "Face captured successfully";
    }
    if (faceCaptureStatus === "failed") {
      return "Face verification failed. Please try again.";
    }
    if (isKioskAwaitingScan) {
      return "Tap to scan face";
    }
    if (userData2?.name !== "Name") {
      return "";
    }
    return isKioskMode ? "" : "Select a person to mark attendance";
  };

  const getCircleStatusColor = () => {
    if (faceCaptureStatus === "verified") return "#2e7d32";
    if (faceCaptureStatus === "failed") return "#d32f2f";
    if (faceCaptureStatus === "verifying" || faceCaptureStatus === "opening") {
      return "text.secondary";
    }
    return "text.secondary";
  };

  const circleStatusMessage = getCircleStatusMessage();
  const organizationDisplayName =
    item?.organization_name ||
    organizationData?.name ||
    org?.organization_name ||
    "Organization";

  const kioskBackButtonSx = {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "10px",
    px: 1.75,
    py: 0.75,
    color: "#374151",
    borderColor: "#d1d5db",
    bgcolor: "#ffffff",
    boxShadow: "none",
    "&:hover": {
      bgcolor: "#f9fafb",
      borderColor: "#9ca3af",
      boxShadow: "none",
    },
    fontSize: { xs: "0.8rem", sm: "0.875rem" },
  };

  const kioskPrimaryOutlineButtonSx = {
    ...kioskBackButtonSx,
    color: "#166534",
    borderColor: "#bbf7d0",
    "&:hover": {
      bgcolor: "#f0fdf4",
      borderColor: "#4caf50",
      boxShadow: "none",
    },
  };

  const workClockRowSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: { xs: 1, sm: 1.5 },
    flexWrap: "nowrap",
    width: "100%",
  };

  return (
    <StyledContainer
      sx={{
        ...(isKioskMode && {
          height: "100%",
          minHeight: 0,
          maxHeight: "100%",
          overflow: isKioskAwaitingScan ? "hidden" : "auto",
          bgcolor: "#f4f6f8",
          padding: isKioskAwaitingScan
            ? { xs: "52px 12px 12px", sm: "56px 16px 12px" }
            : { xs: "52px 16px 16px", sm: "56px 20px 20px" },
        }),
      }}
    >
      {isKioskMode ? (
        <Box
          sx={{
            position: "fixed",
            top: { xs: 14, sm: 16 },
            right: { xs: 14, sm: 16 },
            zIndex: 1300,
          }}
        >
          <Stack direction="row" spacing={1}>
            {!isKioskAwaitingScan && (
              <Button
                variant="outlined"
                onClick={handleNextEmployee}
                sx={kioskPrimaryOutlineButtonSx}
              >
                Next Person
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              onClick={handleBack}
              sx={kioskBackButtonSx}
            >
              Back
            </Button>
          </Stack>
        </Box>
      ) : (
      <Stack
        direction="row"
        spacing={2}
        sx={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 1300,
          flexWrap: "wrap",
          justifyContent: "flex-end",
          "@media (max-width:600px)": {
            position: "static",
            justifyContent: "center",
            mt: 1,
          },
        }}
      >
        {showChangeOrganization && (
          <Button
            variant="contained"
            color="error"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: "8px",
              padding: "6px 16px",
              backgroundColor: "blue",
              boxShadow: 2,
              "&:hover": {
                backgroundColor: "#2f71d3ff",
                boxShadow: 3,
              },
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
            }}
          >
            Change Organization
          </Button>
        )}

        <Button
          variant="contained"
          color="error"
          startIcon={<LogOutIcon />}
          onClick={handleLogout}
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            borderRadius: "8px",
            padding: "6px 16px",
            boxShadow: 2,
            "&:hover": {
              backgroundColor: "#d32f2f",
              boxShadow: 3,
            },
            fontSize: { xs: "0.8rem", sm: "0.9rem" },
          }}
        >
          Logout
        </Button>
      </Stack>
      )}

      {showToast && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.background.default
                : "#ffffff",
            color: "text.primary",
            padding: "12px 24px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 9999,
            fontSize: "16px",
            fontWeight: "500",
            animation: "slideIn 0.3s ease-out",
          }}
        >
          {toastMessage}
        </div>
      )}

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          ...(isKioskMode && {
            flex: 1,
            justifyContent: "center",
            minHeight: 0,
          }),
          ...(!isKioskMode && {
            maxWidth: 520,
            mx: "auto",
          }),
        }}
      >
      {!isKioskAwaitingScan && !isKioskMode && (
      <HeaderSection sx={{ marginBottom: 1.5, flexShrink: 0 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            fontSize: "16px",
            marginBottom: 1,
          }}
        >
          HR ERP
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            color: "text.secondary",
            marginBottom: 2,
          }}
        >
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.15rem", sm: "1.35rem" },
              color: "text.primary",
            }}
          >
            {organizationDisplayName}
          </Box>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: "bold",
            fontSize: "14px",
            color: "text.primary",
            mb: 1.25,
          }}
        >
          {currentDateTime}
        </Typography>

        {userData2?.name !== "Name" && (
          <Box sx={workClockRowSx}>
            <WorkTimeClock
              stop={
                userData2?.Stop_Cal_Work == true ||
                userData2?.Should_Stop_BreakCal == false
              }
              heading="Total Work Time "
              currentTotal={userData2?.currentTotal}
            />

            <WorkTimeClock
              stop={userData2?.Should_Stop_BreakCal}
              heading="Total Break Time "
              currentTotal={userData2?.TotalBreaks}
            />
          </Box>
        )}
      </HeaderSection>
      )}

      {(!isKioskMode &&
        !hasPreselectedEmployee &&
        !item?.organization_person_id &&
        !item?.employee_id) && (
        <SearchContainer className="search-container">
          <StyledTextField
            fullWidth
            variant="outlined"
            placeholder={
              isKioskMode
                ? "Search employee by code or name..."
                : "Search by employee code..."
            }
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: "#4caf50", marginRight: 1 }} />
              ),
              endAdornment: isSearching && (
                <CircularProgress size={20} sx={{ color: "#4caf50" }} />
              ),
            }}
            size="small"
          />

          {showResults && searchResults?.length > 0 && (
            <SearchResultsContainer elevation={3}>
              <List dense>
                {searchResults?.map((employee, index) => (
                  <SearchResultItem
                    key={employee.id || employee.employee_id || index}
                    onClick={() => handleEmployeeSelect(employee)}
                  >
                    <ListItemText
                      primary={`${employee.Name || employee.name || "Unknown Employee"} (${employee.Employee_Code || employee.employee_code || "No Code"})`}
                      secondary={
                        employee.Designation ||
                        employee.designation ||
                        "Employee"
                      }
                      primaryTypographyProps={{ fontSize: "14px" }}
                      secondaryTypographyProps={{
                        fontSize: "12px",
                        color: "text.primary",
                      }}
                    />
                  </SearchResultItem>
                ))}
              </List>
            </SearchResultsContainer>
          )}

          {showResults &&
            searchResults.length === 0 &&
            !isSearching &&
            searchQuery.trim() && (
              <SearchResultsContainer elevation={3}>
                <Box sx={{ padding: 2, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.primary", fontSize: "12px" }}
                  >
                    {'No employees found matching "'}
                    {searchQuery}
                    {'"'}
                  </Typography>
                </Box>
              </SearchResultsContainer>
            )}
        </SearchContainer>
      )}

      {isKioskAwaitingScan && (
        <HeaderSection sx={{ marginBottom: 2, flexShrink: 0, textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.15rem", sm: "1.35rem" },
              color: "#111827",
              marginBottom: 1,
            }}
          >
            {organizationDisplayName}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontSize: "14px",
              color: "#6b7280",
            }}
          >
            {currentDateTime}
          </Typography>
        </HeaderSection>
      )}

      {isKioskMode && !isKioskAwaitingScan && (
        <HeaderSection sx={{ marginBottom: 1.5, flexShrink: 0, textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.05rem", sm: "1.2rem" },
              color: "#111827",
              marginBottom: 0.75,
            }}
          >
            {organizationDisplayName}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontSize: "14px",
              color: "#6b7280",
              mb: 1.25,
            }}
          >
            {currentDateTime}
          </Typography>
          {userData2?.name !== "Name" && (
            <Box sx={workClockRowSx}>
              <WorkTimeClock
                stop={
                  userData2?.Stop_Cal_Work == true ||
                  userData2?.Should_Stop_BreakCal == false
                }
                heading="Total Work Time "
                currentTotal={userData2?.currentTotal}
              />
              <WorkTimeClock
                stop={userData2?.Should_Stop_BreakCal}
                heading="Total Break Time "
                currentTotal={userData2?.TotalBreaks}
              />
            </Box>
          )}
        </HeaderSection>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: isKioskMode ? "row" : "column",
          gap: 2,
          flexWrap: "wrap",
          width: "100%",
          maxWidth: isKioskMode ? 760 : 420,
          flexShrink: 0,
          mx: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flexShrink: 0,
            position: "relative",
          }}
        >
        <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircleContainer
          onClick={
            isKioskAwaitingScan ? handlePhotoClick : undefined
          }
          sx={{
            cursor: isKioskAwaitingScan ? "pointer" : "default",
            width: isKioskAwaitingScan ? { xs: 230, sm: 270 } : 168,
            height: isKioskAwaitingScan ? { xs: 230, sm: 270 } : 168,
            margin: isKioskAwaitingScan
              ? { xs: "12px 0 0", sm: "16px 0 0" }
              : undefined,
            flexShrink: 0,
            borderWidth: isKioskAwaitingScan ? "3px" : "2px",
          }}
        >
          {isCameraActive ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "50%",
              }}
            />
          ) : faceCaptureStatus === "verifying" ? (
            <CircularProgress size={36} sx={{ color: "#4caf50" }} />
          ) : faceCaptureStatus === "verified" ? (
            <CameraAltIcon
              sx={{
                fontSize: isKioskAwaitingScan ? 78 : 60,
                color: "#2e7d32",
              }}
            />
          ) : userData2?.profileImage &&
            userData2.profileImage !== "Profile_Image_Url" ? (
            <img
              src={userData2.profileImage || "/placeholder.svg"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "50%",
              }}
            />
          ) : (
            <CameraAltIcon
              sx={{
                fontSize: isKioskAwaitingScan ? 78 : 60,
                color: "#4caf50",
              }}
            />
          )}
        </CircleContainer>
        </Box>

        {circleStatusMessage ? (
        <Typography
          variant="body2"
          sx={{
            mt: 1.25,
            px: 2,
            textAlign: "center",
            lineHeight: 1.35,
            fontSize: isKioskAwaitingScan ? "13px" : "11px",
            fontWeight:
              isKioskAwaitingScan || faceCaptureStatus ? 600 : 500,
            color: getCircleStatusColor(),
            maxWidth: isKioskAwaitingScan ? 300 : 240,
          }}
        >
          {circleStatusMessage}
        </Typography>
        ) : null}
        </Box>

      </Box>

      {cameraError && (
        <Box
          sx={{
            textAlign: "center",
            width: "100%",
            maxWidth: 280,
            mx: "auto",
            alignSelf: "center",
            mb: 0.5,
            mt: 0.25,
            px: 1,
            py: 0.35,
            backgroundColor: "#ffebee",
            borderRadius: "6px",
            border: "1px solid #ffcdd2",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#d32f2f",
              fontSize: { xs: "9.5px", sm: "10.5px" },
              lineHeight: 1.2,
              fontWeight: 500,
            }}
          >
            {cameraError}
          </Typography>
        </Box>
      )}

      {!isKioskAwaitingScan && (
        <>
      <Box sx={{ textAlign: "center", marginBottom: 1.25, flexShrink: 0, width: "100%" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            color: "text.primary",
            marginBottom: 0.25,
          }}
        >
          {userData2.name}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {getDisplayedRole()}
        </Typography>
      </Box>

      <ButtonContainer sx={{ marginTop: 1.25, flexShrink: 0, width: "100%", justifyContent: "center" }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.6 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "10.5px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "62px",
              minHeight: "22px",
              backgroundColor: "#111827",
              color: "#ffffff !important",
              borderRadius: "999px",
              px: 1.1,
              py: 0.32,
              lineHeight: 1.15,
              fontWeight: 700,
              letterSpacing: "0.3px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.28)",
              visibility: clockInDisplayTime ? "visible" : "hidden",
            }}
          >
            {clockInDisplayTime || "--:--"}
          </Typography>
          <ActionButton
            variant="outlined"
            onClick={handleClockIn}
            disabled={!allowed.clockin || buttonsDisabled || isLoading || isCameraActive}
            sx={{
              opacity: !allowed.clockin ? 0.7 : 1,
              backgroundColor: !allowed.clockin
                ? theme.palette.background.default
                : "#ffffff",
              color: !allowed.clockin ? "#2e7d32" : "#333",
              cursor: !allowed.clockin ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: !allowed.clockin
                  ? theme.palette.background.default
                  : "#f0f8f0",
              },
            }}
          >
            Clock
            <br />
            In
          </ActionButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.6 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "10.5px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "62px",
              minHeight: "22px",
              backgroundColor: "#111827",
              color: "#ffffff !important",
              borderRadius: "999px",
              px: 1.1,
              py: 0.32,
              lineHeight: 1.15,
              fontWeight: 700,
              letterSpacing: "0.3px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.28)",
              visibility: breakStartDisplayTime ? "visible" : "hidden",
            }}
          >
            {breakStartDisplayTime || "--:--"}
          </Typography>
          <ActionButton
            variant="outlined"
            onClick={handleStartBreak}
            disabled={!allowed.breakstart || buttonsDisabled || isLoading || isCameraActive}
            sx={{
              opacity: !allowed.breakstart ? 0.7 : 1,
              backgroundColor: !allowed.breakstart
                ? theme.palette.background.default
                : "white",
              color: !allowed.breakstart ? "#2e7d32" : "#333",
              cursor: !allowed.breakstart ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: !allowed.breakstart ? "#e8f5e8" : "#f0f8f0",
              },
            }}
          >
            Break
            <br />
            Start
          </ActionButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.6 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "10.5px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "62px",
              minHeight: "22px",
              backgroundColor: "#111827",
              color: "#ffffff !important",
              borderRadius: "999px",
              px: 1.1,
              py: 0.32,
              lineHeight: 1.15,
              fontWeight: 700,
              letterSpacing: "0.3px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.28)",
              visibility: breakEndDisplayTime ? "visible" : "hidden",
            }}
          >
            {breakEndDisplayTime || "--:--"}
          </Typography>
          <ActionButton
            variant="outlined"
            onClick={handleEndBreak}
            disabled={!allowed.breakend || buttonsDisabled || isLoading || isCameraActive}
            sx={{
              opacity: !allowed.breakend ? 0.7 : 1,
              backgroundColor: !allowed.breakend
                ? theme.palette.background.default
                : "white",
              color: !allowed.breakend ? "#2e7d32" : "#333",
              cursor: !allowed.breakend ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: !allowed.breakend ? "#e8f5e8" : "#f0f8f0",
              },
            }}
          >
            Break
            <br />
            End
          </ActionButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.6 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "10.5px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "62px",
              minHeight: "22px",
              backgroundColor: "#111827",
              color: "#ffffff !important",
              borderRadius: "999px",
              px: 1.1,
              py: 0.32,
              lineHeight: 1.15,
              fontWeight: 700,
              letterSpacing: "0.3px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.28)",
              visibility: clockOutDisplayTime ? "visible" : "hidden",
            }}
          >
            {clockOutDisplayTime || "--:--"}
          </Typography>
          <ActionButton
            variant="outlined"
            onClick={handleClockOut}
            disabled={!allowed.clockout || buttonsDisabled || isLoading || isCameraActive}
            sx={{
              opacity: !allowed.clockout ? 0.7 : 1,
              backgroundColor: !allowed.clockout
                ? theme.palette.background.default
                : "white",
              color: !allowed.clockout ? "#2e7d32" : "#333",
              cursor: !allowed.clockout ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: !allowed.clockout ? "#e8f5e8" : "#f0f8f0",
              },
            }}
          >
            Clock
            <br />
            Out
          </ActionButton>
        </Box>
      </ButtonContainer>
        </>
      )}

      </Box>

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </StyledContainer>
  );
};

async function fetchFilterEmployeeStatus(opts) {
  try {
    const { MAIN_URL, organizationId, token, organizationPersonId, employeeId, employeeCode, name } =
      opts;
    if (!organizationId) throw new Error("Missing organization id");

    const url = new URL(
      `${MAIN_URL}/api/organizations/${organizationId}/filter-employee`
    );
    // Prioritize ID-based searches - only use name if no IDs are provided
    if (organizationPersonId) {
      url.searchParams.set("organization_person_id", String(organizationPersonId));
    } else if (employeeId) {
      url.searchParams.set("employee_id", String(employeeId));
    } else if (employeeCode) {
      url.searchParams.set("employee_code", String(employeeCode));
    } else if (name) {
      // Only use name as last resort
      url.searchParams.set("q", String(name));
    }

    const res = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`filter-employee failed: ${res.status} ${text}`);
    }

    const data = await res.json();

    // Normalize various possible response shapes into a single flat array
    let list = [];
    if (Array.isArray(data)) {
      list = data;
    } else if (Array.isArray(data?.data)) {
      list = data.data;
    } else if (Array.isArray(data?.employees)) {
      list = data.employees;
    } else if (Array.isArray(data?.[0])) {
      list = data[0];
    } else if (data) {
      list = [data];
    }

    // If we have an organizationPersonId or employeeId, try to find the exact match in the list
    // Note: The API returns 'id' field which IS the organization_person_id
    const searchId = organizationPersonId || employeeId;
    if (searchId && Array.isArray(list)) {
      const targetId = String(searchId);
      const match = list.find((e) => {
        // The 'id' field in API response IS the organization_person_id
        // Priority: id (organization_person_id) > organization_person_id > employee_id
        const employeeOrgPersonId = e?.id || e?.organization_person_id;
        const employeeId = e?.employee_id || e?.Employee_ID;
        
        // Check if id (organization_person_id) matches
        if (employeeOrgPersonId && String(employeeOrgPersonId) === targetId) {
          return true;
        }
        
        // Also check employee_id as fallback
        if (employeeId && String(employeeId) === targetId) {
          return true;
        }
        
        return false;
      });
      
      if (match) {
        console.log("[fetchFilterEmployeeStatus] Found exact match:", {
          targetId,
          matchedId: match.id || match.organization_person_id,
          employeeName: match?.Name || match?.name,
          employeeCode: match?.Employee_Code
        });
        return match;
      }
      
      // If no exact match found, log warning but return first item as fallback
      console.warn("[fetchFilterEmployeeStatus] No exact match found for organization_person_id:", targetId, "Available IDs:", list.map(e => ({ id: e.id, name: e.Name || e.name })));
      return Array.isArray(list) && list.length > 0 ? list[0] : null;
    }

    // If no search ID provided, return first item (for search functionality)
    return Array.isArray(list) ? list[0] : list;
  } catch (err) {
    if (err.message && err.message.includes("401")) {
      toast.error("Session Expired!");
      window.location.href = "/login";
      return;
    }
    if (err.response && err.response.status === 401) {
      toast.error("Session Expired!");
      window.location.href = "/login";
      return;
    }
    console.error("Error in fetchFilterEmployeeStatus:", err);
    toast.error(err.message || "Unexpected error occurred");
  }
}

async function fetchFilterInternStatus(opts) {
  try {
    const { MAIN_URL, organizationId, token, organizationPersonId, internId, internCode, name } =
      opts;
    if (!organizationId) throw new Error("Missing organization id");

    const url = new URL(
      `${MAIN_URL}/api/organizations/${organizationId}/filter-intern`
    );
    if (organizationPersonId) {
      url.searchParams.set("organization_person_id", String(organizationPersonId));
    } else if (internId) {
      url.searchParams.set("intern_id", String(internId));
    } else if (internCode) {
      url.searchParams.set("intern_code", String(internCode));
    } else if (name) {
      url.searchParams.set("q", String(name));
    }

    const res = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`filter-intern failed: ${res.status} ${text}`);
    }

    const data = await res.json();

    let list = [];
    if (Array.isArray(data)) {
      list = data;
    } else if (Array.isArray(data?.data)) {
      list = data.data;
    } else if (Array.isArray(data?.interns)) {
      list = data.interns;
    } else if (Array.isArray(data?.[0])) {
      list = data[0];
    } else if (data) {
      list = [data];
    }

    const searchId = organizationPersonId || internId;
    if (searchId && Array.isArray(list)) {
      const targetId = String(searchId);
      const match = list.find((e) => {
        const internOrgPersonId = e?.id || e?.organization_person_id;
        const matchedInternId = e?.intern_id || e?.Intern_ID;

        if (internOrgPersonId && String(internOrgPersonId) === targetId) {
          return true;
        }

        if (matchedInternId && String(matchedInternId) === targetId) {
          return true;
        }

        return false;
      });

      if (match) {
        return match;
      }

      return Array.isArray(list) && list.length > 0 ? list[0] : null;
    }

    return Array.isArray(list) ? list[0] : list;
  } catch (err) {
    if (err.message && err.message.includes("401")) {
      toast.error("Session Expired!");
      window.location.href = "/login";
      return;
    }
    console.error("Error in fetchFilterInternStatus:", err);
    toast.error(err.message || "Unexpected error occurred");
  }
}

export default FaceAuth;
